import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  styled,
  tableCellClasses,
  Stack,
  TablePagination,
  useMediaQuery,
  useTheme,
  Avatar,
  Chip,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import SelectInput from "../../Components/InputComponents/SelectInput";
import StarIcon from "@mui/icons-material/Star";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const COLORS = [
  "#0088FE", // blue
  "#FF8042", // orange
  "#00C49F", // teal
  "#FFBB28", // yellow-orange
  "#A28BFF", // purple
  "#FF5A87", // pink-red
  "#4BC0C0", // aqua
  "#C9CB3B", // olive green
  "#6A4C93", // deep violet
  "#36A2EB", // sky blue
  "#9966FF", // deep purple
  "#FF6384", // hot pink
  "#66BB6A", // green
  "#FFD54F", // light amber
  "#8D6E63", // brown
];

const StatCard = React.memo(({ title, value, Icon, color }) => (
  <Card
    sx={{
      display: "flex",
      flexDirection: "column",
      boxShadow: "none",
      // border: "2px solid #f0f0f0",
      borderRadius: 2,
      height: "100%",
      bgcolor: color || "#f0f0f0",
      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.34)",
    }}
  >
    <CardContent
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Geist, sans-serif",
            fontSize: "18px",
            color: "#1c1c1c",
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            background: "white",
            border: "2px solid lightgrey",
            borderRadius: "50%",
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {Icon}
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: "Geist, sans-serif",
          fontSize: "22px",
          color: "#212121",
          fontWeight: 600,
        }}
      >
        {value ?? "--"}
      </Typography>
    </CardContent>
  </Card>
));

const JobsChart = ({
  data,
  data1, // [{ Country, ApplicationCount }]
  data2, // [{ Profession, TotalJobsPosted }]
  data3,
  yearOptions = [],
  year,
  setYear,
}) => {
  return (
    <Paper sx={{ boxShadow: "none" }}>
      <Grid container>
        {/* Country in Process */}
        <Grid item xs={12} sm={12} md={6} my={1}>
          <Box
            sx={{
              border: "2px solid lightgrey",
              borderRadius: 2,
              p: 2,
              my: 1,
              mx: { xs: 0, md: 1 },
              minHeight: { xs: "0px", md: "400px" },
            }}
          >
            <Typography
              sx={{
                fontFamily: "Geist, sans-serif",
                fontSize: "18px",
                color: "#3e3e3e",
                fontWeight: 500,
                letterSpacing: "0.5px",
              }}
              mb={1}
            >
              Country in Progress
            </Typography>
            {data1?.every((item) => item.TotalJobsPosted == 0) ? (
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                sx={{
                  minHeight: "420px", // matches chart height so it’s truly centered
                }}
              >
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: "40px", background: "white" }}
                >
                  No data to show
                </Typography>
              </Grid>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  top: "15px",
                }}
              >
                <ResponsiveContainer width="100%" height={420}>
                  <BarChart
                    data={data1}
                    layout="vertical"
                    margin={{ top: 5, right: 10, left: 30, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      dataKey="TotalJobsPosted"
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                         fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <YAxis
                      type="category"
                      dataKey="Country"
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                         fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="TotalJobsPosted"
                      fill="#006CB8"
                      name="Jobs Posted"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        </Grid>

        {/* New Job Openings */}
        <Grid item xs={12} sm={12} md={6} my={1}>
          <Box
            sx={{
              border: "2px solid lightgrey",
              borderRadius: 2,
              p: 2,
              my: 1,
              mx: { xs: 0, md: 1 },
              minHeight: { xs: "0px", md: "400px" },
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                sm={8}
                md={8}
                lg={8}
                sx={{ minHeight: "50px" }}
              >
                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    fontSize: "18px",
                    color: "#3e3e3e",
                    fontWeight: 500,
                    letterSpacing: "0.5px",
                  }}
                >
                  New Job Openings
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4} mb={2}>
                <SelectInput
                  label="Year"
                  options={yearOptions}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  fullWidth
                />
              </Grid>

              {data?.every(
                (item) => item.TotalJobsPosted == 0 && item.TotalOpenings == 0
              ) ? (
                <Grid
                  item
                  xs={12}
                  mb={2}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "383px", // ensures vertical centering space
                  }}
                >
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: "40px", background: "white" }}
                  >
                    No data to show
                  </Typography>
                </Grid>
              ) : (
                <Grid
                  item
                  xs={12}
                  sm={12}
                  sx={{
                    position: "relative",
                    right: { xs: "0px", sm: "25px" },
                  }}
                >
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="MonthName"
                        tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                         fontFamily: "Geist, sans-serif",
                      }}
                      />
                      <YAxis
                        tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                         fontFamily: "Geist, sans-serif",
                      }}
                      />
                      <Tooltip />
                      <Legend />
                      {/* First bar for Jobs Posted */}
                      <Bar
                        dataKey="TotalJobsPosted"
                        fill="#006CB8"
                        name="Jobs Posted"
                      />
                      {/* Second bar for Openings */}
                      <Bar
                        dataKey="TotalOpenings"
                        fill="#E26C09"
                        name="Total Openings"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>

        {/* Professions based Stat */}
        <Grid item xs={12} sm={12} md={6}>
          <Box
            sx={{
              border: "2px solid lightgrey",
              borderRadius: 2,
              p: 2,
              my: 1,
              mx: { xs: 0, md: 1 },
              minHeight: { xs: "0px", md: "400px" },
            }}
          >
            <Typography
              sx={{
                fontFamily: "Geist, sans-serif",
                fontSize: "18px",
                color: "#3e3e3e",
                fontWeight: 500,
                letterSpacing: "0.5px",
              }}
              mb={1}
            >
              Professions based Stat
            </Typography>

            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{ width: "100%" }}
            >
              {/* Chart Section */}
              {!data2 || data2.every((item) => item.TotalJobsPosted === 0) ? (
                <Box
                  sx={{
                    height: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    marginTop:"15px"
                  }}
                >
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: "40px", background: "white" }}
                  >
                    No data to show
                  </Typography>
                </Box>
              ) : (
                <>
                  <Grid item xs={12} md={12} lg={12} xl={7}>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={data2}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          dataKey="TotalJobsPosted"
                          nameKey="Profession"
                          label={{
                            // fill: "#000",
                            fontSize: 14,
                            fontWeight: "bold",
                          }} // Pie labels
                        >
                          {data2.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Grid>

                  <Grid item xs={12} md={12} lg={12} xl={5}>
                    <Box sx={{ pl: { xs: 0, md: 2 } }}>
                      {data2
                        ?.filter((entry) => entry.TotalJobsPosted !== 0)
                        ?.map((entry, index) => (
                          <Box
                            key={entry.Profession}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                backgroundColor: COLORS[index % COLORS.length],
                                borderRadius: "50%",
                                mr: 1,
                              }}
                            />
                            <Typography
                              sx={{
                                fontFamily: "Geist, sans-serif",
                                letterSpacing: `0.5px`,
                                fontSize: "14px",
                                color: "#333",
                                fontWeight: "500",
                              }}
                            >
                              {entry.Profession} ({entry.TotalJobsPosted})
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </Grid>

        {/* Client Vs Jobs */}
        <Grid item xs={12} sm={12} md={6}>
          <Box
            sx={{
              border: "2px solid lightgrey",
              borderRadius: 2,
              p: 2,
              my: 1,
              mx: { xs: 0, md: 1 },
              minHeight: {
                xs: "0px",
                sm: "0px",
                md: "400px",
                lg: "400px",
                xl: "400px",
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: "Geist, sans-serif",
                fontSize: "18px",
                color: "#3e3e3e",
                fontWeight: 500,
                letterSpacing: "0.5px",
              }}
              mb={1}
            >
              Client Vs Jobs
            </Typography>
            {data3?.every((item) => item?.JobPostCount == 0) ? (
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                sx={{
                  minHeight: "300px", // matches chart height so it’s truly centered
                }}
              >
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: "40px", background: "white" }}
                >
                  No data to show
                </Typography>
              </Grid>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  top: "15px",
                }}
              >
                <ResponsiveContainer width="100%" minHeight={300}>
                  <BarChart
                    data={data3}
                    layout="vertical"
                    margin={{ top: 5, right: 25, left: 25, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      dataKey="JobPostCount"
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                         fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <YAxis
                      type="category"
                      dataKey="CD_Name"
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                         fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="JobPostCount"
                      fill="#006CB8"
                      name="Jobs Posted"
                      label={{
                        fill: "black",
                        fontSize: 12,
                        fontWeight: "bold",
                        fontFamily: "Geist, sans-serif",
                        position: "right", // top, insideTop, insideBottom, etc.
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

const JobReportView = ({
  jobStatData,
  jobApplicationData,
  deployedJobStats,
  yearOptions,
  year,
  setYear,
  jobPostStats,
  jobProfStats,
  jobClientStats,
}) => {
  const top4 = (deployedJobStats ?? [])
    .slice()
    .sort((a, b) => (b?.DeployedCount ?? 0) - (a?.DeployedCount ?? 0))
    .slice(0, 4);

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        mb={1}
        px={1}
      >
        Job Reports
      </Typography>

      <Grid container spacing={2} p={1}>
        {jobStatData.map((item, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={idx}>
            <StatCard
              title={item.title}
              value={item.value}
              Icon={item?.Icon}
              color={item?.color}
            />
          </Grid>
        ))}
      </Grid>

      {top4?.length > 0 ? (
        <>
          <Grid container spacing={2} p={1}>
            <Grid item xs={12}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
                my={1}
              >
                Most deployed Job
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={5} p={1} justifyContent="flex-start" mb={3}>
            {top4.map((a, index) => {
              const labels = ["1st", "2nd", "3rd", "4th"];

              return (
                <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={index}>
                  <Box
                    sx={{
                      position: "relative",
                      boxShadow: 3,
                      borderRadius: 2,
                      p: 2,
                      textAlign: "center",
                      color: "white",
                      backgroundColor:
                        index == 0
                          ? `#367A91`
                          : index == 1
                          ? `#38446B`
                          : index == 2
                          ? `#14486C`
                          : index == 3
                          ? `#1B2E3C`
                          : "#1976d2",
                    }}
                  >
                    {/* Circle badge */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: -20,
                        width: 45,
                        height: 45,
                        borderRadius: "50%",
                        backgroundColor: "white",
                        color:
                          index == 0
                            ? `#367A91`
                            : index == 1
                            ? `#38446B`
                            : index == 2
                            ? `#14486C`
                            : index == 3
                            ? `#1B2E3C`
                            : "#1976d2",
                        border: "4px solid",
                        borderColor:
                          index == 0
                            ? `#367A91`
                            : index == 1
                            ? `#38446B`
                            : index == 2
                            ? `#14486C`
                            : index == 3
                            ? `#1B2E3C`
                            : "#1976d2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        boxShadow: `none`,
                        fontSize: "0.9rem",
                      }}
                    >
                      {a?.DeployedCount}
                    </Box>

                    {/* Banner / Ribbon */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        backgroundColor: "white",
                        color: "black",
                        px: 1.5,
                        py: 0.5,
                        fontSize: "0.8rem",
                        fontWeight: "bold",
                        borderRadius: "8px 0px 8px 0px",
                        boxShadow: 3,
                      }}
                    >
                      {labels[index]}
                    </Box>

                    {/* Title */}
                    <Typography
                      sx={{
                        fontFamily: "Geist, sans-serif",
                        letterSpacing: `0.5px`,
                        fontSize: "22px",
                        color: "white",
                        fontWeight: "600",
                        textTransform: "capitalize",
                      }}
                    >
                      {a?.JD_Title}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </>
      ) : null}

      <JobsChart
        data={jobApplicationData}
        data1={jobPostStats}
        data2={jobProfStats}
        data3={jobClientStats}
        yearOptions={yearOptions}
        year={year}
        setYear={setYear}
      />
    </Box>
  );
};

export default JobReportView;
